import React, { useState } from 'react';
import { StepPartnerRegistration } from './components/StepPartnerRegistration';
import { StepProgress } from './components/StepProgress';
import { StepStoreRegistration } from './components/StepStoreRegistration';
import { StepDiscountRegistration } from './components/StepDiscountRegistration';
import { MainTemplate } from '../../templates/MainTemplate';
import { Box, Button, Heading, Icon, Stack, VStack, Container, Card, CardBody, HStack } from '@chakra-ui/react';
import { Text } from '../../components/atoms/Text';
import { FaCheck, FaExternalLinkAlt, FaPlus } from 'react-icons/fa';
import { baseUrl } from '@/service/axios';

// Interface para os dados do wizard
interface IWizardData {
  token: string;
  companyMode: 'online' | 'local';
  // Dados do usuário (Step 1)
  user: {
    id?: number;
    full_name?: string;
    email?: string;
    isPartner?: boolean;
    companyId?: number;
  };
  isNewUser?: boolean;

  // Dados da empresa (Step 2)
  company: {
    id?: number;
    cnpj?: string;
    corporate_name?: string;
    name?: string;
    category?: string;
    description?: string;
    cellphone?: string;
    site?: string;
    cover_photo?: string;
    logo?: string;
    status?: 'pending' | 'approved' | 'rejected';
  };

  // Dados da loja/endereço (Step 2)
  store: {
    postal_code?: string;
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    state?: string;
    city?: string;
    country?: string;
  };

  // Dados do desconto (Step 3)
  discount: {
    id?: number;
    discount?: string;
    title_discount?: string;
    link_desconto?: string;
    cupom_desconto?: string;
    mode?: 'local' | 'online';
    type?: 'buy_and_gain' | 'treat' | 'discount';
    image?: string;
    rules?: {
      cumulativa?: boolean;
      delivery?: boolean;
      promocao?: boolean;
      disponibilidade?: boolean;
      outros?: boolean;
      outrosText?: string;
    };
    rulesText?: string;
    status?: 'pending' | 'approved' | 'rejected';
  };
}

export function PartnerWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  
  const isStaging = baseUrl?.includes('staging');
  
  const [wizardData, setWizardData] = useState<IWizardData>({
    user: {},
    isNewUser: false,
    company: {},
    companyMode: 'local',
    token: '',
    store: {
      country: 'Brasil',
    },
    discount: {
      mode: 'local',
      type: 'discount',
      rules: {},
    },
  });

  // Funções de navegação
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeWizard = () => {
    setIsComplete(true);
  };

  // Função para atualizar dados do wizard
  const updateData = (newData: Partial<IWizardData>) => {
    setWizardData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const steps = [
    {
      number: 1,
      title: 'Autenticação',
      description: 'Login ou registro',
    },
    {
      number: 2,
      title: 'Empresa',
      description: 'Dados da empresa e loja',
    },
    {
      number: 3,
      title: 'Desconto',
      description: 'Configurar oferta',
    },
  ];

  if (isComplete) {
    return (
      <MainTemplate>
        <Container maxW="4xl" py={8}>
          <VStack spacing={8} textAlign="center">
            {/* Card principal de sucesso */}
            <Card w="full" shadow="xl" borderRadius="xl" overflow="hidden">
              <CardBody p={8}>
                <VStack spacing={6}>
                  {/* Ícone de sucesso animado */}
                  <Box
                    w={24}
                    h={24}
                    bg="green.100"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 0 30px rgba(72, 187, 120, 0.3)"
                    animation="pulse 2s infinite"
                  >
                    <Icon as={FaCheck} w={12} h={12} color="green.600" />
                  </Box>

                  {/* Título e descrição */}
                  <VStack spacing={4}>
                    <Heading size="xl" color="gray.900" fontFamily="Glory" fontWeight="bold">
                      🎉 Cadastro Concluído com Sucesso!
                    </Heading>
                    <Text color="gray.600" fontSize="lg" maxW="2xl" lineHeight="tall">
                      Seu cadastro foi enviado para análise. Em breve você receberá um e-mail com o status da aprovação.
                    </Text>
                  </VStack>

                  {/* Informações da empresa */}
                  {wizardData.company?.name && (
                    <Box 
                      bg="blue.50" 
                      p={6} 
                      borderRadius="xl" 
                      w="full" 
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      <VStack spacing={3}>
                        <Heading size="md" color="blue.900" fontFamily="Glory">
                          📋 Dados do Cadastro
                        </Heading>
                        <HStack spacing={8} flexWrap="wrap" justify="center">
                          <VStack spacing={1}>
                            <Text fontSize="sm" color="blue.700" fontWeight="medium">
                              Empresa
                            </Text>
                            <Text fontSize="lg" color="blue.900" fontWeight="bold">
                              {wizardData.company.name}
                            </Text>
                          </VStack>
                          {wizardData.user?.email && (
                            <VStack spacing={1}>
                              <Text fontSize="sm" color="blue.700" fontWeight="medium">
                                Email
                              </Text>
                              <Text fontSize="lg" color="blue.900" fontWeight="bold">
                                {wizardData.user.email}
                              </Text>
                            </VStack>
                          )}
                        </HStack>
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            

             {/* Card com ações */}
             <Card w="full" shadow="lg" borderRadius="xl">
               <CardBody p={6}>
                 <VStack spacing={6}>
                   <Heading size="lg" color="gray.800" fontFamily="Glory">
                     🚀 Ações Disponíveis
                   </Heading>
                   
                   <VStack spacing={4} w="full">
                     {wizardData.token && (
                       <Button
                         onClick={() => window.open(`https://pdv.${isStaging ? 'beta.' : ''}partiu.com.br/login/${wizardData.token}`, '_blank')}
                         backgroundColor="orange.500"
                         size="lg"
                         px={8}
                         color="white"
                         py={4}
                         fontSize="md"
                         _hover={{
                           backgroundColor: "orange.600",
                           transform: 'translateY(-2px)',
                           boxShadow: 'lg',
                         }}
                         transition="all 0.2s"
                         leftIcon={<FaExternalLinkAlt />}
                       >
                         Acessar PDV
                       </Button>
                     )}

                     <Button
                       onClick={() => {
                         setCurrentStep(1);
                         setIsComplete(false);
                         setWizardData({
                           user: {},
                           isNewUser: false,
                           company: {},
                           companyMode: 'local',
                           token: '',
                           store: {
                             country: 'Brasil',
                           },
                           discount: {
                             mode: 'local',
                             type: 'discount',
                             rules: {},
                           },
                         });
                       }}
                       backgroundColor="blue.500"
                       size="lg"
                       px={8}
                       py={4}
                       fontSize="md"
                       color="white"
                       _hover={{
                         backgroundColor: "blue.600",
                         transform: 'translateY(-2px)',
                         boxShadow: 'lg',
                       }}
                       transition="all 0.2s"
                       leftIcon={<FaPlus />}
                     >
                       Fazer Novo Cadastro
                     </Button>

                   </VStack>
                 </VStack>
               </CardBody>
             </Card>
          </VStack>
        </Container>
      </MainTemplate>
    );
  }

  return (
    <MainTemplate>
      <Stack mt={10} w="full" mx="auto">
        <Box w="full" maxW="4xl" mx="auto" p={6}>
          <StepProgress steps={steps} currentStep={currentStep} />
        </Box>

        {currentStep === 1 && <StepPartnerRegistration onNext={nextStep} data={wizardData} updateData={updateData} />}
        {currentStep === 2 && (
          <StepStoreRegistration onNext={nextStep} onPrevious={prevStep} data={wizardData} updateData={updateData} />
        )}

        {currentStep === 3 && (
          <StepDiscountRegistration
            onPrevious={prevStep}
            onComplete={completeWizard}
            data={wizardData}
            updateData={updateData}
          />
        )}
        <Box w="full" maxW="4xl" mx="auto" height={100} />
      </Stack>
    </MainTemplate>
  );
}
